/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { PaginationModes } from "@/api/AppApi/services/company/interfaces/ICompany";
import {
  useGetAllProductsInfiniteQuery,
  useGetAllProductsQuery,
} from "@/api/AppApi/services/product";
import ProductGrid from "@/app/components/common-builder/product/product-grids/ProductGrid";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "@/app/components/lib/CustomButton";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useEffect, useMemo } from "react";
import { Spinner } from "@/utils/bootstrap";
import { useIntersectionObserver as useInView } from "@/hooks/useIntersectionObserver";
import Paginate from "../pagination/Paginate";
import ProductContainer from "./ProductContainer";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import SearchNoResult from "../../search-page/SearchNoResult";
import Filter from "@/app/components/Filter";
import InlineFilterRow from "@/app/components/common-filter-builder/inline-filters/InlineFilterRow";
import ViewCategoryPixel from "../../category-page/ViewCategoryPixel";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { getFbcCookie, getFbpCookie } from "@/utils/functions/cookieOperations";
import { v4 as uuidv4 } from "uuid";

interface props {
  categoryId?: number | string;
  searchParams: { [key: string]: string };
  isCategoryPage: boolean;
  PixeListName: string;
  PixeListId: string;
  /**
   * First page of products fetched on the SERVER.
   *
   * Passed as a prop rather than relying on a dehydrated react-query entry: the
   * client hook derives its cache key from cookies via `cookies-next`, which does
   * not resolve to the same values while the component is server-rendered, so a
   * prefetched entry is never found and the grid renders empty. Props are
   * deterministic. Used only until the client query resolves.
   */
  initialProducts?: any[];
}

// Shared with the server-side prefetch so both build the identical cache key.
import { productQuery } from "./productQuery";

const ProductsWithFilters: React.FC<props> = ({
  categoryId,
  searchParams,
  isCategoryPage,
  PixeListName,
  PixeListId,
  initialProducts,
}) => {
  const t = useTranslation();
  const [ref, isInView] = useInView({ rootMargin: "1200px" });
  const getCompanty = useGetCompanyQuery();
  const { pagination_mode } =
    getCompanty?.data?.result?.theme?.components?.body ?? {};

  const isInfiniteScroll = pagination_mode === PaginationModes?.scroll;
  const isButtonScroll = pagination_mode === PaginationModes?.button;

  const queryParams = { ...productQuery(searchParams, categoryId!) };
  const q = searchParams.q ?? "";
  // One shared event_id (+ fbc/fbp) per list view — reused by the browser
  // ViewContentList/Search pixel and sent to the /products backend for CAPI dedup.
  // Stable per view; regenerates when the category or filters change.
  // Kept separate from queryParams so they don't bust the React Query cache key.
  const trackingParams = useMemo(() => {
    const fbcCookie = (getFbcCookie() as string) ?? null;
    const fbpCookie = (getFbpCookie() as string) ?? null;
    const id = uuidv4();
    return { fbc: fbcCookie, fbp: fbpCookie, event_id: id };
  }, [q, categoryId, JSON.stringify(searchParams)]);

  const isEvent = queryParams?.sub_type === "event";

  const { data: PaginatProductsData, isFetching: isPaginatProductsLoading } =
    useGetAllProductsQuery({
      params: { ...queryParams },
      trackingParams,
      enabled: !isInfiniteScroll && !isButtonScroll,
    });
  const PaginatProducts = PaginatProductsData?.result?.data;

  const getProducts = useGetAllProductsInfiniteQuery({
    params: { ...queryParams },
    trackingParams,
    enabled: isInfiniteScroll || isButtonScroll,
  });
  const clientProducts =
    !isInfiniteScroll && !isButtonScroll
      ? PaginatProducts
      : getProducts?.data?.pages.map((page) => page.result?.data).flat()!;
  // Fall back to the server-rendered first page until the client query resolves.
  // This is what puts real product markup — and crawlable links — in the initial
  // HTML; without it the grid rendered empty on the server.
  //
  // `??`, NOT a length check: once the client query has resolved, an empty array
  // is a REAL answer ("no products match this filter") and must win. Treating it
  // as "not loaded yet" left the stale server list on screen forever and made
  // `SearchNoResult` / `EmptyCategory` / `EmptyProductsFilter` unreachable.
  const products = clientProducts ?? initialProducts;

  // The server-rendered grid is on screen and the client query has not answered
  // yet. `ProductContainer` swaps children for a spinner whenever `loading` is
  // true, so reporting the initial hydration fetch as "loading" would rip the SSR
  // grid — and its LCP image — straight back out of the DOM. Subsequent fetches
  // (filter, sort, page) still show the spinner, because by then `clientProducts`
  // is defined.
  const showingServerFallback = !clientProducts && !!initialProducts?.length;
  const isProductLoading = isPaginatProductsLoading && !showingServerFallback;
  const theme = getCompanty?.data?.result?.theme!;
  const size = isEvent
    ? Size.large
    : theme?.components?.product?.product_box_style!;
  const { GTagSearchEventTracher } = useEventTrackers();

  useEffect(() => {
    if (!isInfiniteScroll) return;
    if (!isInView) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    if (getProducts.hasNextPage && !getProducts.isFetchingNextPage) {
      timeoutId = setTimeout(() => {
        getProducts.fetchNextPage();
      }, 50);
    }
    return () => clearTimeout(timeoutId);
  }, [
    isInView,
    isInfiniteScroll,
    getProducts.hasNextPage,
    getProducts.isFetchingNextPage,
  ]);

  useEffect(() => {
    if (!q) return;
    if (isProductLoading) return;
    GTagSearchEventTracher(q, !!products?.length, trackingParams?.event_id);
  }, [q]);

  if (!isCategoryPage && !isProductLoading && products?.length === 0)
    return <SearchNoResult q={q} />;

  return (
    <>
      <Filter
        productNum={products?.length ?? 0}
        searchParams={searchParams}
        category_id={categoryId}
      />
      <ProductContainer
        productsLenght={products?.length || 0}
        loading={isProductLoading}
        isInfiniteScrollLoading={getProducts.isFetching}
        isCategoryPage={!!isCategoryPage}
      >
        <InlineFilterRow searchParams={searchParams} categoryId={categoryId} />
        <ProductGrid
          products={{
            data: [...(products ?? [])],
            configurations: { style_type: size },
          }}
          theme={theme}
          showCustomEventData={isEvent}
          // This grid is the first content on category/search pages, so its
          // opening row holds the LCP image — load it eagerly instead of waiting
          // for the intersection observer. Kept small (one mobile row) so the
          // eager images don't compete with each other.
          priorityCount={2}
        />
        {isInfiniteScroll ? (
          <div ref={ref} className="w-100 d-flex justify-content-center mt-3">
            {getProducts.hasNextPage && <Spinner />}
          </div>
        ) : isButtonScroll ? (
          <div className="mt-4 d-flex justify-content-center">
            {getProducts.hasNextPage && (
              <CustomButton
                style={{
                  background: "var(--add-to-cart-bg-color)",
                }}
                onClick={() => {
                  if (
                    getProducts.hasNextPage &&
                    !getProducts.isFetchingNextPage
                  ) {
                    getProducts.fetchNextPage();
                  }
                }}
                type={BtnTypes.primary}
                size={BtnSizes.medium}
                radius={BtnRadius.medium}
              >
                {!getProducts?.isFetching ? t.loadMore : <Spinner />}
              </CustomButton>
            )}
          </div>
        ) : (
          <Paginate
            pageCount={PaginatProductsData?.result?.meta?.last_page || 1}
          />
        )}
      </ProductContainer>

      {isCategoryPage && products && (
        <ViewCategoryPixel
          products={products}
          item_list_name={PixeListName}
          item_list_id={PixeListId}
          eventId={trackingParams.event_id}
        />
      )}
    </>
  );
};
export default ProductsWithFilters;
